/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { useEffect, useMemo } from 'react';

import { Inline } from '@atlaskit/primitives/compiled';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import { is3pProductKey, type ProductKeys } from '../../../../common/constants/products';
import AppFilterTabsInline from '../../../../common/ui/filters/app-filter-tabs-inline';
import { triggerExposureForRightRailImprovements } from '../../../../common/utils/experiments';
import { useSafeIntercom } from '../../../../common/utils/use-safe-intercom';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { useSelectFilter } from '../../../../controllers/filters/base-select-filter';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import {
	useBootstrap,
	useFeatures,
	useIsNounsAvailable,
} from '../../../../controllers/store/bootstrap';
import { useSearchActions, useSelectedProduct } from '../../../../controllers/store/search';

/**
 * Render search page product filters as tabs.
 */
const AppFilterInline = (): React.JSX.Element => {
	const [{ products: availableProducts, nouns: availableNouns, thirdPartyConfigs }] =
		useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const [isNounsAvailable] = useIsNounsAvailable();
	const [universalTypeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });

	const { selectProductFilter, unselectProductFilter, unsafe_setAtlasNouns } = useSearchActions();
	const intercom = useSafeIntercom();
	const [features] = useFeatures();

	// Trigger an exposure once when the component is mounted.
	useEffect(() => {
		triggerExposureForRightRailImprovements();
	}, []);

	const appFilterProps = useMemo(
		() => ({
			sideBySide: features?.sideBySide?.enabled,
			availableProducts,
			availableNouns,
			selectedProduct,
			isNounsAvailable,
			filterData: universalTypeFilterData,
			setAtlasNoun: unsafe_setAtlasNouns,
			onSelectProduct: (newProduct: ProductKeys | undefined) => {
				if (newProduct === selectedProduct) {
					// do nothing when the same product is selected
					return;
				}

				if (!newProduct && selectedProduct) {
					unselectProductFilter();
					return;
				}

				selectProductFilter(newProduct);

				if (newProduct && is3pProductKey(newProduct)) {
					intercom?.trackEvent('product_filter_clicked_3p', {
						product_filter: newProduct,
					});
				} else {
					intercom?.trackEvent('product_filter_clicked', {
						product_filter: newProduct,
					});
				}
			},
			useOAuthHandlers,
			useIsUserOAuthed,
			thirdPartyConfigs,
		}),
		[
			features,
			availableProducts,
			availableNouns,
			selectedProduct,
			isNounsAvailable,
			universalTypeFilterData,
			unsafe_setAtlasNouns,
			selectProductFilter,
			unselectProductFilter,
			intercom,
			thirdPartyConfigs,
		],
	);

	return (
		<Inline space="space.100" shouldWrap grow="fill">
			<ErrorBoundary section="SearchPageAppFilterInline">
				{features?.sideBySide?.enabled ? <></> : <AppFilterTabsInline {...appFilterProps} />}
			</ErrorBoundary>
		</Inline>
	);
};

export default AppFilterInline;
